import React from 'react'
import {useSelector} from 'react-redux'
import {useCachedSearchTopOffers} from 'components/Accommodation/Offers/useCachedSearchTopOffers'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getAnchorPriceNightlyPerRoom} from 'modules/sapiSearch/selectors'
import {RootState} from 'store'

import {useDeviceLayout} from '@daedalus/atlas/context/deviceLayout'
import {ContentWrapper} from '@daedalus/atlas/helpers/ContentWrapper'
import {Flex} from '@daedalus/atlas/helpers/Flex'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {getDisplayPrice} from '@daedalus/core/src/price/business/price'
import {getSplitBookingDates} from '@daedalus/core/src/room/business/utils/getSplitBookingDates'
import {getSplitBookingRooms} from '@daedalus/core/src/room/business/utils/getSplitBookingRooms'
import {SplitBooking} from '@daedalus/core/src/room/types/room'
import {useGetHotelQuery} from '@daedalus/core/src/sapi/services/searchApi'
import {toDecreasePercentage} from '@daedalus/core/src/utils/number'
import {SplitBookingTimeline} from '@daedalus/shared/src/booking/SplitBooking/SplitBookingTimeline'
import {BookingTransitionMessage} from '@daedalus/shared/src/booking/SplitBooking/SplitBookingTransitionMessage'
import {SplitBookingCardHeader as CardHeader} from '@daedalus/shared/src/booking/SplitBookingCardHeader'
import {SplitBookingCardSubtitle as CardSubtitle} from '@daedalus/shared/src/booking/SplitBookingCardSubtitle'

import {RoomCardVariant} from '../RoomVariant/RoomCardVariant'
import {RoomCardVariants} from '../RoomVariant/types'
import {RoomDetailsInfo} from './RoomDetailsInfo'
import {SplitBookingCardFooter} from './SplitBookingCardFooter'

interface Props {
  splitBooking: SplitBooking
  variant?: RoomCardVariants
  isOnlySplitBookingOfferAvailable?: boolean
  isCheapest?: boolean
}

const CardVariantWrapper: React.FC<{variant: RoomCardVariants}> = ({
  variant,
  children
}) => {
  return <RoomCardVariant variant={variant}>{children}</RoomCardVariant>
}

export const SplitBookingCard = ({
  splitBooking,
  variant,
  isCheapest,
  isOnlySplitBookingOfferAvailable = false
}: Props) => {
  const hotelId = useSelector(getAccommodationPageHotelId)
  const {offerEntity} = useCachedSearchTopOffers({
    hotelId
  })
  const splitBookingRooms = getSplitBookingRooms(splitBooking)

  const splitBookingDates = getSplitBookingDates(splitBooking)
  const {checkIn, checkOut, splitDate} = splitBookingDates

  const {data: hotel} = useGetHotelQuery({hotelId}, {skip: !hotelId})
  const {bundleId, links, offers, prices, cancellationPenalties, totalRate} =
    splitBooking
  const {isMobile} = useDeviceLayout()
  const [{offer: firstOffer}, {offer: secondOffer}] = offers

  const displayPrice = getDisplayPrice(prices)
  const nightlyRate = displayPrice.chargeable.nightlyDisplayTotal

  const anchorPrice = useSelector((state: RootState) =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const savingPercentage = toDecreasePercentage(
    nightlyRate,
    anchorPrice,
    'floor'
  )

  const timelineDates: [string, string, string] | undefined = !isMobile
    ? [checkIn, splitDate, checkOut]
    : undefined
  // displaying images when at least one room has image
  const showImages = splitBookingRooms.some(({images}) => images.length)

  return (
    <CardVariantWrapper variant={variant}>
      <ContentWrapper padding={isMobile ? 's500' : 's600'}>
        <Flex.Column gap={isMobile ? 's500' : 's600'}>
          <Flex.Column gap="s100">
            <CardHeader
              isOnlySplitBookingOfferAvailable={
                isOnlySplitBookingOfferAvailable
              }
              savingPercentage={savingPercentage}
            />
            <CardSubtitle
              isOnlySplitBookingOfferAvailable={
                isOnlySplitBookingOfferAvailable
              }
              isCheapest={isCheapest}
              hasCustomStyle={isMobile}
            />
          </Flex.Column>

          <SplitBookingTimeline
            customGap
            topComponent={
              <RoomDetailsInfo
                room={splitBookingRooms[0]}
                date={checkIn}
                midDate={splitDate}
                isCheckInDateAvailable
                services={firstOffer?.services}
                placement="top"
                showImage={showImages}
              />
            }
            bottomComponent={
              <RoomDetailsInfo
                room={splitBookingRooms[1]}
                date={checkOut}
                midDate={splitDate}
                services={secondOffer?.services}
                placement="bottom"
                showImage={showImages}
              />
            }
            middleComponent={
              <BookingTransitionMessage
                midDate={splitDate}
                checkIn={checkIn}
                checkOut={checkOut}
                title={
                  <FormattedMessageWrapper
                    id="splitBooking.switchRoomTitle"
                    defaultMessage="Switch rooms to save"
                  />
                }
                message={
                  isCheapest && !isOnlySplitBookingOfferAvailable ? (
                    <FormattedMessageWrapper
                      id="splitBooking.switchRoomSubtitle"
                      defaultMessage="We got you the lowest price by splitting your stay across two separate bookings. Here’s how it works:"
                    />
                  ) : (
                    <FormattedMessageWrapper
                      id="splitBooking.roomSwitchTooltipSubtitle"
                      defaultMessage="This stay is split between two separate bookings. Here’s how it works:"
                    />
                  )
                }
              />
            }
            dates={timelineDates}
          />
        </Flex.Column>
      </ContentWrapper>

      <SplitBookingCardFooter
        hotelId={hotelId}
        cancellationPenalties={cancellationPenalties}
        hotelCurrencyCode={hotel?.currency}
        hotelTimeZone={hotel?.timezone}
        totalPrice={totalRate?.base + totalRate?.taxes + totalRate?.hotelFees}
        chargeableCurrencyCode={displayPrice.chargeable.currency}
        prices={splitBooking.prices}
        splitBookingDates={splitBookingDates}
        splitBookingRooms={splitBookingRooms}
        bundleId={bundleId}
        links={links}
        cug={firstOffer.cug}
        providerCode={firstOffer.providerCode}
        providerRateType={firstOffer.providerRateType}
        isCheapest={isCheapest}
        isSelected={splitBooking.isClicked}
      />
    </CardVariantWrapper>
  )
}
